/**
 * batchResult — pure normalization of a batch-mutation endpoint response
 * (A5-194). Kept JSX/hook-free and dependency-light so it is unit-testable
 * under Node's `--experimental-strip-types` runner; `useBatchMutation`
 * (in `useBatchMutation.ts`) re-exports it.
 */

/** Per-item outcome returned by the batch endpoint (or synthesized client-side). */
export interface BatchItemResult<TItem, TResult> {
  /** The submitted item. */
  item: TItem;
  /** Whether the server accepted this item. */
  success: boolean;
  /** The per-item server result, when successful. */
  result?: TResult;
  /** The per-item error message, when failed. */
  error?: string;
}

/** Aggregate result of a batch submission. */
export interface BatchResult<TItem, TResult> {
  results: BatchItemResult<TItem, TResult>[];
  successCount: number;
  failureCount: number;
  /** True when every item succeeded. */
  allSucceeded: boolean;
  /** True when at least one item succeeded and at least one failed. */
  partial: boolean;
}

/** Shape the `/<endpoint>/batch` endpoint is expected to return. */
export interface BatchEndpointResponse<TResult> {
  results?: Array<{ success?: boolean; result?: TResult; error?: string }>;
}

/**
 * Normalize an arbitrary batch endpoint response into the canonical
 * {@link BatchResult}. Pure + exported so it is unit-testable without React.
 *
 * Accepts either a `{ results: [...] }` envelope (per-item success/error) or a
 * bare result array (treated as all-success). Items the endpoint returns no row
 * for are marked failed so the caller never silently loses an item.
 */
export function normalizeBatchResult<TItem, TResult>(
  items: TItem[],
  response: BatchEndpointResponse<TResult> | TResult[] | unknown,
): BatchResult<TItem, TResult> {
  const rows: Array<{ success?: boolean; result?: TResult; error?: string }> = Array.isArray(
    (response as BatchEndpointResponse<TResult>)?.results,
  )
    ? (response as BatchEndpointResponse<TResult>).results!
    : Array.isArray(response)
      ? (response as TResult[]).map((r) => ({ success: true, result: r }))
      : [];

  const results: BatchItemResult<TItem, TResult>[] = items.map((item, i) => {
    const row = rows[i];
    if (!row) {
      return { item, success: false, error: 'No result returned for item' };
    }
    const success = row.success !== false && row.error === undefined;
    return {
      item,
      success,
      result: row.result,
      error: success ? undefined : (row.error ?? 'Item rejected'),
    };
  });

  const successCount = results.filter((r) => r.success).length;
  const failureCount = results.length - successCount;
  return {
    results,
    successCount,
    failureCount,
    allSucceeded: failureCount === 0 && successCount > 0,
    partial: successCount > 0 && failureCount > 0,
  };
}
